/* Budget — three buckets fed entirely by live data: Inventory restock (Spending),
 * Rainy-day transfers (Savings) and bill locks due this month (Bills). No manual categories. */
import { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Wallet, PiggyBank, Receipt, Refrigerator, ExternalLink, Lock, Umbrella } from "lucide-react";
import { Link } from "react-router-dom";
import SavingsProjectionCard from "@/components/SavingsProjectionCard";

const BUCKETS = [
  { key: "spending", label: "Spending", icon: Wallet, color: "rgb(var(--c-gold))", desc: "Groceries & supplies to restock, live from Inventory" },
  { key: "savings",  label: "Savings",  icon: PiggyBank, color: "rgb(var(--c-forest))", desc: "Everything you've moved into your Rainy-day fund" },
  { key: "bills",    label: "Bills",    icon: Receipt, color: "rgb(var(--c-clay))", desc: "Bill locks due this month, live from Vaults" },
];

export default function Budget() {
  const [income, setIncome] = useState(0);
  const [restock, setRestock] = useState(null);
  const [rainyTransferred, setRainyTransferred] = useState(0);
  const [billLocks, setBillLocks] = useState({ total: 0, count: 0, items: [] });
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const r = await api.get("/budget");
      setIncome(r.data.monthly_income || 0);
      setRainyTransferred(Number(r.data.rainy_day_transferred || 0));
      setBillLocks(r.data.bill_locks_this_month || { total: 0, count: 0, items: [] });
      api.get("/inventory").then(({ data }) => setRestock(data)).catch(() => setRestock(null));
    } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const saveIncome = async () => {
    try {
      const { data } = await api.post("/budget", { monthly_income: Number(income) || 0, categories: [] });
      setIncome(data.monthly_income);
    } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
  };

  const restockAmount = Number(restock?.total_to_restock || 0);
  const totals = BUCKETS.map((b) => ({
    ...b,
    total: b.key === "spending" ? restockAmount : b.key === "savings" ? rainyTransferred : billLocks.total,
  }));
  const allocated = restockAmount + billLocks.total;
  const remaining = income - allocated;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Budget</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Tell the money where to go.</h1>
        <p className="text-moss mt-3 max-w-xl">
          Three buckets — <em>Spending</em>, <em>Savings</em>, and <em>Bills</em> — filled automatically from your Inventory, your Rainy-day fund and your bill locks. Nothing to type in except your income.
        </p>
        {error && <div className="mt-4 text-sm text-clay" data-testid="budget-error">{error}</div>}

        <div className="mt-8 grid md:grid-cols-4 gap-4" data-testid="budget-buckets">
          <div className="bg-ink text-cream rounded-2xl p-6 md:col-span-1" data-testid="income-card">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Monthly income</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-mono text-2xl">$</span>
              <input type="number" min="0" step="0.01" value={income}
                onChange={(e) => setIncome(parseFloat(e.target.value) || 0)}
                onBlur={saveIncome}
                className="bg-transparent border-b border-sage/40 font-mono text-3xl w-full outline-none focus:border-gold"
                data-testid="income-input" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="uppercase tracking-[0.2em] font-semibold text-sage">Allocated</div>
                <div className="font-mono text-lg mt-1" data-testid="allocated-total">{fmtMoney(allocated)}</div>
              </div>
              <div>
                <div className="uppercase tracking-[0.2em] font-semibold text-sage">Remaining</div>
                <div className={`font-mono text-lg mt-1 ${remaining < 0 ? "text-clay" : "text-gold"}`} data-testid="remaining-total">{fmtMoney(remaining)}</div>
              </div>
            </div>
          </div>

          {totals.map((t) => {
            const Icon = t.icon;
            const pct = income > 0 ? Math.min(100, (t.total / income) * 100) : 0;
            return (
              <div key={t.key} className="bg-linen border border-sand rounded-2xl p-6" data-testid={`bucket-${t.key}`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-5 h-5" style={{ color: t.color }} strokeWidth={1.5} />
                  <span className="text-xs font-mono text-sage">{income > 0 ? `${pct.toFixed(0)}%` : "—"}</span>
                </div>
                <div className="font-serif text-xl mt-3">{t.label}</div>
                <div className="text-xs text-sage">{t.desc}</div>
                <div className="font-mono text-3xl mt-4 text-ink" data-testid={`bucket-total-${t.key}`}>{fmtMoney(t.total)}</div>
                {t.key === "bills" && (
                  <div className="text-[11px] text-clay mt-1" data-testid="bucket-bills-locks">
                    {billLocks.count} bill lock{billLocks.count === 1 ? "" : "s"} due this month
                  </div>
                )}
                {t.key === "savings" && (
                  <div className="text-[11px] text-forest mt-1" data-testid="bucket-savings-rainy">Moved into your Rainy-day fund so far</div>
                )}
                {t.key === "spending" && (
                  <div className="text-[11px] text-gold-deep mt-1" data-testid="bucket-spending-restock">{restock ? `${restock.low_count} item${restock.low_count === 1 ? "" : "s"} running low` : "Add items on Inventory"}</div>
                )}
                <div className="mt-3 h-1.5 bg-sand rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: t.color }} />
                </div>
              </div>
            );
          })}
        </div>

        <section className="mt-12" data-testid="budget-sources">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Where the numbers come from</div>
          <h2 className="font-serif text-2xl mt-1">Live, not typed.</h2>
          <div className="mt-4 space-y-2">
            <SourceRow icon={Refrigerator} color={BUCKETS[0].color} title="Groceries & supplies · restock" sub={restock ? `${restock.low_count} running low · counts toward Spending` : "Nothing tracked yet"} amount={restockAmount} to="/inventory" linkLabel="Inventory" testid="source-restock" />
            <SourceRow icon={Umbrella} color={BUCKETS[1].color} title="Rainy-day fund transfers" sub="Every deposit, payday pull and sweep into your Rainy-day fund · counts toward Savings" amount={rainyTransferred} to="/dashboard" linkLabel="Vaults" testid="source-rainy" />
            <SourceRow icon={Lock} color={BUCKETS[2].color} title={`Bill locks due this month${billLocks.count ? ` · ${billLocks.count}` : ""}`} sub={billLocks.items.length ? billLocks.items.map((b) => `${b.name} ${fmtMoney(b.amount)} · due ${b.due_date.slice(5)}`).join("  ·  ") : "No bill locks due this month"} amount={billLocks.total} to="/dashboard" linkLabel="Bill locks" testid="source-bills" />
          </div>
        </section>

        <SavingsProjectionCard />
      </div>
    </div>
  );
}

function SourceRow({ icon: Icon, color, title, sub, amount, to, linkLabel, testid }) {
  return (
    <div className="bg-linen border border-sand rounded-xl p-4 grid grid-cols-12 gap-3 items-center" data-testid={testid}>
      <div className="col-span-12 sm:col-span-8 flex items-center gap-3 min-w-0">
        <Icon className="w-4 h-4 shrink-0" style={{ color }} strokeWidth={1.5} />
        <div className="min-w-0">
          <div className="font-medium text-ink truncate">{title}</div>
          <div className="text-[11px] text-sage truncate">{sub}</div>
        </div>
      </div>
      <div className="col-span-12 sm:col-span-4 flex items-center justify-between sm:justify-end gap-3">
        <span className="font-mono text-lg text-ink" data-testid={`${testid}-amount`}>{fmtMoney(amount)}</span>
        <Link to={to} className="inline-flex items-center gap-1 text-xs text-forest hover:text-ink" data-testid={`${testid}-link`}><ExternalLink className="w-3 h-3" /> {linkLabel}</Link>
      </div>
    </div>
  );
}
